import { useState, useEffect } from 'react';
import { useHLSPlayer } from './useHLSPlayer';
import { useFrameCounter } from './useFrameCounter';
import { ErrorMessage } from './ErrorMessage';
import { PlayButton } from './controls/PlayButton';
import { FrameNavigationButtons } from './controls/FrameNavigationButtons';
import { SpeedControl } from './controls/SpeedControl';
import { AnnotationButton } from './controls/AnnotationButton';
import { FrameCounter } from './controls/FrameCounter';
import { Loader } from 'lucide-react';

interface VideoPlayerProps {
  url: string;
  keyPairId: string;
  policy: string;
  signature: string;
  onAnnotationClick: () => void;
  onVideoRef: (ref: HTMLVideoElement | null) => void;
  onTimeUpdate: (time: number) => void;
  onDurationChange: (duration: number) => void;
  isAnnotationOpen: boolean;
}

export function VideoPlayer({ 
  url, 
  keyPairId,
  policy,
  signature,
  onAnnotationClick,
  onVideoRef,
  onTimeUpdate,
  onDurationChange,
  isAnnotationOpen
}: VideoPlayerProps) {
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showFrameCounter, setShowFrameCounter] = useState(false);

  const {
    videoRef,
    isLoading,
    isPlaying,
    error,
    play,
    pause,
    setPlaybackSpeed: setHlsPlaybackSpeed,
    stepForward,
    stepBackward,
    jumpForward,
    jumpBackward,
  } = useHLSPlayer(url, keyPairId, policy, signature);

  const currentFrame = useFrameCounter(videoRef);

  // Handle video reference and events
  useEffect(() => {
    if (videoRef.current) {
      onVideoRef(videoRef.current);
      
      const handleTimeUpdate = () => {
        onTimeUpdate(videoRef.current?.currentTime || 0);
      };
      
      const handleDurationChange = () => {
        onDurationChange(videoRef.current?.duration || 0);
      };

      videoRef.current.addEventListener('timeupdate', handleTimeUpdate);
      videoRef.current.addEventListener('durationchange', handleDurationChange);

      return () => {
        videoRef.current?.removeEventListener('timeupdate', handleTimeUpdate);
        videoRef.current?.removeEventListener('durationchange', handleDurationChange);
      };
    }
  }, [videoRef, onVideoRef, onTimeUpdate, onDurationChange]);

  // Handle frame counter visibility
  useEffect(() => {
    let timeout: number;
    if (showFrameCounter) {
      timeout = window.setTimeout(() => setShowFrameCounter(false), 2000);
    }
    return () => clearTimeout(timeout);
  }, [currentFrame, showFrameCounter]);

  // Pause video when annotation is opened
  useEffect(() => {
    if (isAnnotationOpen && isPlaying) {
      pause();
    }
  }, [isAnnotationOpen, isPlaying, pause]);

  const handleFrameNavigation = (action: () => void) => {
    setShowFrameCounter(true);
    action();
  };

  const handleSpeedChange = (speed: number) => {
    setPlaybackSpeed(speed);
    setHlsPlaybackSpeed(speed);
  };

  const handleAnnotationClick = () => {
    if (!isAnnotationOpen && isPlaying) {
      pause();
    } else if (isAnnotationOpen && !isPlaying) {
      play();
    }
    onAnnotationClick();
  };

  if (error) {
    return <ErrorMessage message={error} />;
  }

  return (
    <div className="video-player">
      {isLoading && (
        <div className="loading">
          <Loader size={24} className="animate-spin" />
          <span>Loading stream...</span>
        </div>
      )}
      <video ref={videoRef} />
      <FrameCounter 
        currentFrame={currentFrame} 
        visible={showFrameCounter}
      />
      
      <div className="video-controls-overlay">
        <div className="controls-group">
          <PlayButton 
            isPlaying={isPlaying} 
            onPlay={play} 
            onPause={pause} 
          />
          <FrameNavigationButtons
            onStepBackward={() => handleFrameNavigation(stepBackward)}
            onStepForward={() => handleFrameNavigation(stepForward)}
            onJumpBackward={() => handleFrameNavigation(jumpBackward)}
            onJumpForward={() => handleFrameNavigation(jumpForward)}
          />
        </div>
        <div className="controls-group right">
          <SpeedControl 
            speed={playbackSpeed}
            onSpeedChange={handleSpeedChange}
          />
          <AnnotationButton 
            onClick={handleAnnotationClick}
            isOpen={false}
          />
        </div>
      </div>
    </div>
  );
}